import { Component, Injector, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, Validators } from '@angular/forms';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { SwisslifeLocationService } from '@app/services/swisslife-location.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

const FISCAL_ADDRESS_REQUIRED_FIELDS = ['street', 'city', 'country'];

@UntilDestroy()
@Component({
    selector: 'address-form',
    templateUrl: './address-form.component.html',
    styleUrls: ['./address-form.component.scss'],
    standalone: false
})
export class AddressFormComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  countries: any[];
  departments: any[];
  isSwisslifeProduct: boolean = false;
  isEuodiaVieProduct: boolean = false;
  productType = '';

  private formRegenerated$ = new Subject<void>();

  constructor(
    protected formConstructor: PrFormConstructor,
    private referenceDao: ReferenceDao,

    private subscriptionFacade: SubscriptionFacadeService,
    private swisslifeLocation: SwisslifeLocationService,
    protected injector: Injector,
  ) {
    super(formConstructor, injector);
  }

  ngOnInit() {
    this.productType = this.subscriptionFacade?.getPledge()?.productType;
    this.isEuodiaVieProduct = this.getHasPledgeContainsEuodiaVieProduct();
    this.isSwisslifeProduct = this.swisslifeLocation.hasSwisslifeProduct(
      this.subscriptionFacade.getPledge(),
    );

    combineLatest([this.referenceDao.getCountries()])
      .pipe(untilDestroyed(this))
      .subscribe(([countries]) => {
        this.countries = countries;
      });
    this.subscriptionFacade.initForm(this);
  }

  initForm(entity: any, options?: any) {
    super.initForm(entity, options);
    this.formValueChanges();
  }

  getHasPledgeContainsEuodiaVieProduct() {
    const pledge = this.subscriptionFacade.getPledge();
    return pledge?.products
      ?.filter(product => !!product?.productName)
      ?.some(product => 'euodia vie' === product?.productName.toLowerCase());
  }

  formValueChanges() {
    this.formRegenerated$.next();

    this.owners.controls.forEach(ownerControl => {
      ownerControl
        .get('fiscalAddressIsNotContactAddress')
        .valueChanges.pipe(
          takeUntil(this.formRegenerated$),
          untilDestroyed(this),
        )
        .subscribe(fiscalAddressIsNotContactAddress => {
          this.applyFiscalAddressValidators(
            ownerControl,
            !!fiscalAddressIsNotContactAddress,
          );
        });
    });
  }

  /**
   * Rend obligatoires ou facultatifs la rue, la ville et le pays de l'adresse
   * fiscale du détenteur.
   */
  private applyFiscalAddressValidators(
    ownerControl: AbstractControl,
    required: boolean,
  ) {
    FISCAL_ADDRESS_REQUIRED_FIELDS.forEach(field => {
      const control = ownerControl.get('fiscalAddress').get(field);
      control.setValidators(
        required ? [Validators.required] : [Validators.nullValidator],
      );
      control.updateValueAndValidity();
    });
  }

  onSubmit() {
    this.form.markAsDirty();

    this.subscriptionFacade.submitPledge(this);
  }

  get owners() {
    return this.form.get('owners') as UntypedFormArray;
  }

  next() {}

  prev() {
    this.subscriptionFacade.previousStep();
  }
}
